import * as React from 'react';

import { cn } from '../lib/utils';

import { staggerOffsetCells } from './bead-grid-geometry';

/**
 * Cuadrícula de mostacillas dibujada sobre `<canvas>`.
 *
 * Es presentacional puro: recibe la rejilla como números y los colores como
 * cadenas, y no sabe qué es un patrón ni una paleta. No importa nada del
 * dominio (Artículo II.4).
 *
 * Se dibuja en canvas y no en DOM porque un diseño máximo son 15 000 celdas, y
 * 15 000 nodos no sostienen el zoom ni el desplazamiento en un teléfono de gama
 * media. Solo se pintan las filas visibles.
 */

export interface BeadGridCanvasProps extends React.ComponentProps<'canvas'> {
  /** Cada fila es un array de índices de `colors`, o `null` si la celda está vacía. */
  readonly rows: readonly (readonly (number | null)[])[];
  readonly colors: readonly string[];
  readonly columns: number;
  /** Lado de la mostacilla en px CSS: es el nivel de zoom. */
  readonly cellSize: number;
  readonly offsetX: number;
  readonly offsetY: number;
  /** Ancho de la franja izquierda con los números de fila. */
  readonly gutterWidth: number;
  /**
   * Corre media mostacilla las filas impares, como los ladrillos de una pared.
   * Es una propiedad de dibujo: el componente no sabe qué tejido la motiva.
   */
  readonly staggered?: boolean;
  readonly width: number;
  readonly height: number;
  /** Fila resaltada, por ejemplo mientras su menú está abierto. */
  readonly highlightedRow?: number | null;
  /**
   * Cuántas filas están dadas por tejidas, contadas desde arriba. Se dibujan
   * bajo un velo translúcido que deja ver el color (RF-36, RF-39).
   */
  readonly completedRowCount?: number;
  /**
   * Fila que toca tejer, o `null` si no hay ninguna. Lleva un marco de acento
   * por fuera de las celdas, para destacarla sin alterar ningún color (RF-38).
   *
   * Convive con `highlightedRow`, que es del menú de fila del modo diseño. Los
   * dos modos son excluyentes, y si llegaran juntas manda esta.
   */
  readonly currentRow?: number | null;
  /**
   * Hacia dónde se ensarta la fila actual, para dibujar la punta de flecha
   * (RF-28f). Solo se usa cuando hay fila actual.
   */
  readonly currentRowRightToLeft?: boolean;
  readonly label: string;
  readonly description: string;
}

interface Palette {
  readonly ink: string;
  readonly muted: string;
  readonly surface: string;
  readonly gutter: string;
  readonly gridLine: string;
  readonly emptyCell: string;
  readonly highlight: string;
  readonly accent: string;
}

/**
 * Los tokens del tema son variables CSS, y el canvas no las entiende: hay que
 * resolverlas a valores concretos antes de dibujar.
 */
function readPalette(element: HTMLElement): Palette {
  const styles = getComputedStyle(element);
  const token = (name: string, fallback: string): string =>
    styles.getPropertyValue(name).trim() || fallback;

  return {
    ink: token('--foreground', '#2b2430'),
    muted: token('--muted-foreground', '#6b6270'),
    surface: token('--background', '#fdf8fa'),
    gutter: token('--muted', '#f2eef4'),
    gridLine: token('--border', '#e6dfea'),
    emptyCell: token('--card', '#fffdfe'),
    highlight: token('--accent', '#dff0dc'),
    accent: token('--primary', '#7a4f9c'),
  };
}

export function BeadGridCanvas({
  rows,
  colors,
  columns,
  cellSize,
  offsetX,
  offsetY,
  gutterWidth,
  staggered = false,
  width,
  height,
  highlightedRow = null,
  completedRowCount = 0,
  currentRow = null,
  currentRowRightToLeft = true,
  label,
  description,
  className,
  ...props
}: BeadGridCanvasProps) {
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const frameRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const draw = () => {
      // En jsdom no hay contexto 2D: el componente debe montar igual.
      const context = canvas.getContext('2d');
      if (!context) return;

      const ratio = window.devicePixelRatio || 1;
      canvas.width = Math.floor(width * ratio);
      canvas.height = Math.floor(height * ratio);
      context.setTransform(ratio, 0, 0, ratio, 0, 0);

      const palette = readPalette(canvas);

      context.fillStyle = palette.surface;
      context.fillRect(0, 0, width, height);

      // Solo las filas que asoman por la ventana: las demás no se tocan.
      const first = Math.max(0, Math.floor(-offsetY / cellSize));
      const last = Math.min(rows.length - 1, Math.ceil((-offsetY + height) / cellSize));

      const inset = cellSize > 6 ? 1 : 0;

      for (let row = first; row <= last; row += 1) {
        const y = offsetY + row * cellSize;
        const cells = rows[row];
        if (!cells) continue;

        const stagger = staggerOffsetCells(row, staggered) * cellSize;

        if (highlightedRow === row) {
          context.fillStyle = palette.highlight;
          context.fillRect(gutterWidth + offsetX + stagger, y, columns * cellSize, cellSize);
        }

        const rowLeft = gutterWidth + offsetX + stagger;

        for (let column = 0; column < columns; column += 1) {
          const x = rowLeft + column * cellSize;
          if (x + cellSize < gutterWidth || x > width) continue;

          const value = cells[column] ?? null;
          const color = value === null ? null : colors[value];

          if (color) {
            context.fillStyle = color;
            context.fillRect(x + inset, y + inset, cellSize - inset * 2, cellSize - inset * 2);
          } else {
            // La celda vacía se distingue por su borde punteado, no por su
            // color: cualquier relleno chocaría con algún color de la paleta.
            context.fillStyle = palette.emptyCell;
            context.fillRect(x + inset, y + inset, cellSize - inset * 2, cellSize - inset * 2);
            if (cellSize > 8) {
              context.strokeStyle = palette.gridLine;
              context.lineWidth = 1;
              context.strokeRect(x + 0.5, y + 0.5, cellSize - 1, cellSize - 1);
            }
          }
        }

        // Velo sobre lo ya tejido: atenúa sin ocultar, porque la usuaria
        // todavía necesita reconocer el dibujo que lleva hecho (RF-36, RF-39).
        if (row < completedRowCount) {
          context.fillStyle = palette.surface;
          context.globalAlpha = 0.62;
          context.fillRect(rowLeft, y, columns * cellSize, cellSize);
          context.globalAlpha = 1;

          // El tick es forma y no color: es la señal que sobrevive a cualquier
          // daltonismo y al zoom en que el velo casi no se distingue (RF-37).
          if (cellSize >= 14) {
            const cx = rowLeft + columns * cellSize - cellSize * 0.55;
            const cy = y + cellSize / 2;
            const arm = cellSize * 0.18;

            context.strokeStyle = palette.muted;
            context.lineWidth = Math.max(1.5, cellSize * 0.09);
            context.lineCap = 'round';
            context.beginPath();
            context.moveTo(cx - arm, cy);
            context.lineTo(cx - arm * 0.25, cy + arm * 0.75);
            context.lineTo(cx + arm, cy - arm * 0.75);
            context.stroke();
          }
        }
      }

      // El marco de la fila actual se dibuja después de todas las filas para
      // que no lo tape la de abajo, y **por fuera** de las celdas: encima
      // alteraría el color de las mostacillas que la usuaria tiene que ensartar
      // (RF-38). El grosor tiene mínimo en píxeles para seguir viéndose con la
      // celda chica (RF-43).
      if (currentRow !== null && currentRow >= first && currentRow <= last) {
        const y = offsetY + currentRow * cellSize;
        const stagger = staggerOffsetCells(currentRow, staggered) * cellSize;
        const left = gutterWidth + offsetX + stagger;
        const lineWidth = Math.max(2.5, cellSize * 0.12);
        const half = lineWidth / 2;

        context.strokeStyle = palette.accent;
        context.lineWidth = lineWidth;
        context.lineJoin = 'round';
        context.strokeRect(
          left - half,
          y - half,
          columns * cellSize + lineWidth,
          cellSize + lineWidth,
        );

        // Punta de flecha en el extremo por el que se empieza a ensartar, para
        // no obligar a deducir el sentido (RF-28f). Va fuera del marco.
        if (cellSize >= 12) {
          const size = Math.min(cellSize * 0.4, 10);
          const cy = y + cellSize / 2;
          const tip = currentRowRightToLeft
            ? left + columns * cellSize + lineWidth + size
            : left - lineWidth - size;
          const back = currentRowRightToLeft ? tip + size : tip - size;

          context.fillStyle = palette.accent;
          context.beginPath();
          context.moveTo(tip, cy);
          context.lineTo(back, cy - size * 0.7);
          context.lineTo(back, cy + size * 0.7);
          context.closePath();
          context.fill();
        }
      }

      // El gutter va al final para quedar por encima del lienzo desplazado:
      // el número de fila sigue visible en cualquier zoom y desplazamiento.
      context.fillStyle = palette.gutter;
      context.fillRect(0, 0, gutterWidth, height);

      if (cellSize >= 12) {
        context.fillStyle = palette.muted;
        context.font = `${Math.min(12, Math.max(9, cellSize * 0.45))}px system-ui, sans-serif`;
        context.textAlign = 'right';
        context.textBaseline = 'middle';

        // Con celdas pequeñas se numera de cinco en cinco para no amontonar.
        const step = cellSize < 20 ? 5 : 1;
        for (let row = first; row <= last; row += 1) {
          const number = row + 1;
          if (step > 1 && number % step !== 0 && number !== 1) continue;
          context.fillText(
            String(number),
            gutterWidth - 6,
            offsetY + row * cellSize + cellSize / 2,
          );
        }
      }

      context.strokeStyle = palette.gridLine;
      context.beginPath();
      context.moveTo(gutterWidth - 0.5, 0);
      context.lineTo(gutterWidth - 0.5, height);
      context.stroke();
    };

    frameRef.current = window.requestAnimationFrame(draw);

    return () => {
      if (frameRef.current !== null) window.cancelAnimationFrame(frameRef.current);
    };
  }, [
    rows,
    colors,
    columns,
    cellSize,
    offsetX,
    offsetY,
    gutterWidth,
    staggered,
    width,
    height,
    highlightedRow,
    completedRowCount,
    currentRow,
    currentRowRightToLeft,
  ]);

  return (
    <canvas
      ref={canvasRef}
      data-slot="bead-grid-canvas"
      role="img"
      aria-label={label}
      aria-description={description}
      className={cn('block touch-none select-none', className)}
      style={{ width, height }}
      {...props}
    />
  );
}
